import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const Navbar = () => {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { lang, setLang, t } = useLang();
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/thuisbatterijen", label: t.nav.batteries },
    { to: "/zonnepanelen", label: t.nav.solar },
    { to: "/laadpalen", label: t.nav.chargers },
    { to: "/zakelijk", label: t.nav.business },
    { to: "/reviews", label: t.nav.reviews },
    { to: "/over-ons", label: t.nav.about },
  ];

  const secondaryLinks = [
    { to: "/aeg-plug-play", label: "AEG Plug & Play" },
    { to: "/werking", label: t.nav.how },
    { to: "/merken", label: t.nav.brands },
    { to: "/duurzaam-financieren", label: t.nav.finance },
    { to: "/faq", label: t.nav.faq },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60 shadow-[0_4px_20px_rgba(15,23,42,0.04)]"
          : "bg-white/75 backdrop-blur-xl border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 2xl:px-8 h-20 flex items-center justify-between gap-3 xl:gap-4 2xl:gap-6">
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="logo-link">
          <div className="relative">
            <div className="w-9 h-9 rounded-full gradient-pink flex items-center justify-center shadow-[0_4px_14px_rgba(255,47,125,0.4)]">
              <Sun className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="absolute inset-0 w-9 h-9 rounded-full gradient-pink opacity-40 blur-md group-hover:opacity-70 transition-opacity" />
          </div>
          <span className="font-heading font-black text-lg 2xl:text-xl tracking-tight text-navy-900 whitespace-nowrap">Helios Bright</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6 2xl:gap-7 min-w-0">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-brand ${
                location.pathname === l.to ? "text-brand" : "text-navy-800"
              }`}
              data-testid={`nav-link-${l.to.replace(/\//g, "-") || "home"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 xl:gap-3 flex-shrink-0">
          <div className="hidden xl:flex items-center text-xs font-semibold border border-slate-200 rounded-full overflow-hidden bg-white/60 flex-shrink-0">
            <button
              onClick={() => setLang("nl")}
              className={`px-3 py-1.5 transition ${lang === "nl" ? "bg-brand text-white" : "text-navy-800 hover:bg-slate-50"}`}
              data-testid="lang-nl"
            >NL</button>
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition ${lang === "en" ? "bg-brand text-white" : "text-navy-800 hover:bg-slate-50"}`}
              data-testid="lang-en"
            >EN</button>
          </div>
          <Link to="/contact" className="hidden xl:inline-flex btn-pink !text-sm !py-2 !px-4 flex-shrink-0" data-testid="navbar-cta">
            {t.nav.cta}
          </Link>
          <button
            className="xl:hidden p-2 rounded-full bg-white/60 border border-slate-200"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-white border-t border-slate-200"
          >
            <div className="px-5 py-5 flex flex-col gap-3">
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="text-navy-900 font-medium py-2" data-testid={`mobile-nav-${l.to.replace(/\//g, "-") || "home"}`}>
                  {l.label}
                </Link>
              ))}
              <div className="h-px bg-slate-200 my-2" />
              {secondaryLinks.map((l) => (
                <Link key={l.to} to={l.to} className="text-slate-500 text-sm font-medium py-1" data-testid={`mobile-nav-${l.to.replace(/\//g, "-")}`}>
                  {l.label}
                </Link>
              ))}
              <div className="flex items-center gap-2 pt-2">
                <span className="text-xs text-slate-500 mr-2">Taal:</span>
                <button
                  onClick={() => setLang("nl")}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition ${lang === "nl" ? "bg-brand text-white" : "bg-slate-100 text-navy-800"}`}
                  data-testid="mobile-lang-nl"
                >NL</button>
                <button
                  onClick={() => setLang("en")}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition ${lang === "en" ? "bg-brand text-white" : "bg-slate-100 text-navy-800"}`}
                  data-testid="mobile-lang-en"
                >EN</button>
              </div>
              <Link to="/contact" className="btn-pink mt-2" data-testid="mobile-cta">{t.nav.cta}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
